import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Building2, HardHat, Wrench, ArrowRight, ShoppingCart, Zap, ShieldCheck, Users,
} from 'lucide-react';

/**
 * "Try Videra" chooser modal.
 *
 * Videra ships as two very different products and visitors currently have
 * no way to tell them apart. This modal presents the choice clearly:
 *
 *   1. Videra Specialist  (demo.videra.app)
 *      Pre-built sector environments for NHS Capital, PFI Hard Services and
 *      Tier-1 Construction. 7-day fully-unlocked trial. Talk to Keystone
 *      to tailor further.
 *
 *   2. Videra PM  (pm.videra.app)
 *      Everyday project management ready to use right now. 7-day trial or
 *      instant purchase. For any team running projects that need structure.
 */
const TryVideraModal = ({ open, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 animate-[videra-fade-in_.2s_ease-out]"
      onClick={onClose}
      data-testid="try-videra-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden animate-[videra-pop-in_.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-8 pt-8 pb-6 border-b border-slate-200">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
            data-testid="try-videra-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">Choose your Videra</p>
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900 leading-tight">
            Two products. Two trial lengths. Both fully unlocked.
          </h2>
          <p className="mt-2 text-sm md:text-base text-slate-600">
            One is a specialist environment for regulated delivery, on a 14-day trial. The other is ready to run any project, today, on a 7-day trial. Pick the fit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Videra Specialist */}
          <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-slate-200 hover:bg-slate-50 transition-colors" data-testid="try-videra-specialist">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live in production
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-medium text-slate-900 tracking-tight">Videra <span className="text-blue-600">Specialist</span></h3>
            <p className="mt-1 text-sm text-slate-500 font-mono">demo.videra.app</p>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed">
              Pre-built sector environments for regulated, high-stakes delivery. Choose from NHS Capital, PFI Hard Services, Tier-1 Construction, Vanguard assurance and SafeTrace firestopping.
            </p>

            <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Building2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> NHS England / Scotland environments</li>
              <li className="flex items-start gap-2"><Wrench className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> PFI-grade lifecycle, handback and evidence</li>
              <li className="flex items-start gap-2"><HardHat className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Tier-1 Construction governance & controls</li>
              <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Regulator-ready evidence packs</li>
            </ul>

            <div className="mt-6 space-y-2">
              <a
                href="https://demo.videra.app/explore"
                data-testid="try-videra-specialist-cta"
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white px-5 py-3 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors group"
              >
                Try Videra Specialist, 14 days free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link
                to="/contact"
                onClick={onClose}
                data-testid="try-videra-specialist-talk"
                className="w-full inline-flex items-center justify-center gap-1.5 text-slate-700 hover:text-slate-900 text-xs font-medium underline-offset-4 hover:underline py-1"
              >
                Or talk to Keystone about tailoring it
              </Link>
            </div>
            <p className="mt-3 text-[11px] text-slate-500 text-center">Best for: NHS, PFI, Tier-1 construction, large FM</p>
          </div>

          {/* Videra PM */}
          <div className="p-8 md:p-10 hover:bg-slate-50 transition-colors relative" data-testid="try-videra-pm">
            <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.18em] font-bold text-white bg-blue-600 rounded-full px-2.5 py-1">
              Buy today
            </span>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1">
                <Zap className="w-3 h-3" /> Available now
              </span>
            </div>
            <h3 className="text-xl md:text-2xl font-medium text-slate-900 tracking-tight">Videra <span className="text-blue-600">PM</span></h3>
            <p className="mt-1 text-sm text-slate-500 font-mono">pm.videra.app</p>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed">
              Ready-to-run project management for every-day teams. No implementation, no setup call. Sign up, load your projects, invite your team, start today.
            </p>

            <ul className="mt-5 space-y-2.5 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Zap className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Instant access, no setup call needed</li>
              <li className="flex items-start gap-2"><Users className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Invite the whole team, self-serve seats</li>
              <li className="flex items-start gap-2"><ShoppingCart className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Try free for 7 days, or purchase today</li>
              <li className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /> Upgrade to a Specialist environment later</li>
            </ul>

            <div className="mt-6 space-y-2">
              <a
                href="https://pm.videra.app"
                data-testid="try-videra-pm-cta"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-5 py-3 rounded-md text-sm font-medium hover:bg-blue-500 transition-colors group"
              >
                Start Videra PM, 7 days free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a
                href="https://pm.videra.app/pricing"
                data-testid="try-videra-pm-buy"
                className="w-full inline-flex items-center justify-center gap-1.5 text-slate-700 hover:text-slate-900 text-xs font-medium underline-offset-4 hover:underline py-1"
              >
                Or see pricing and buy today
              </a>
            </div>
            <p className="mt-3 text-[11px] text-slate-500 text-center">Best for: everyday project delivery, any sector</p>
          </div>
        </div>

        <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-500">
            Not sure which fits? <button onClick={onClose} className="text-blue-600 hover:underline font-medium">Close</button> and pick <span className="font-medium text-slate-700">Ask Peter, 20 min</span> instead.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TryVideraModal;
